import { useEffect, useState } from "react";
import { getUser, getUsersApi, updateUser } from "@praesto/core";
import { Badge, Card, FormField, Section, SkeletonFormSection, Select } from "@praesto/ui-kit";
import { useCore } from "../lib/core";
import { NotificationsSettings } from "./NotificationsSettings";

interface UserPrefs {
  readonly locale: "it" | "en";
  readonly mfaEnabled: boolean;
}

/** Personal preferences — available to every user (edits only their own record). */
export function PersonalSettings() {
  const core = useCore();
  const t = core.i18n.t;
  const uid = core.auth.currentUserId();
  const [prefs, setPrefs] = useState<UserPrefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const { user } = (await getUser(api.apiBase, uid, tok)) as {
        user: {
          locale?: "it" | "en";
          mfaEnabled?: boolean;
        };
      };
      if (!cancelled) {
        setPrefs({
          locale: user.locale ?? "it",
          mfaEnabled: user.mfaEnabled ?? false,
        });
      }
    })()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  async function save(next: UserPrefs) {
    if (!uid) return;
    setPrefs(next);
    setStatus("saving");
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateUser(api.apiBase, uid, { locale: next.locale }, tok);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    }
  }

  if (loading) return <SkeletonFormSection fields={4} />;
  if (!prefs) return <p className="text-sm text-danger">{t("settings.error")}</p>;

  return (
    <div className="space-y-6">
      <Section width="readable">
        <div className="h-6 flex items-center">
          {status === "saving" && (
            <Badge tone="neutral" variant="soft" size="xs">
              {t("settings.saving")}
            </Badge>
          )}
          {status === "saved" && (
            <Badge tone="success" variant="soft" size="xs">
              {t("settings.saved")}
            </Badge>
          )}
          {status === "error" && (
            <Badge tone="danger" variant="soft" size="xs">
              {t("settings.error")}
            </Badge>
          )}
        </div>
      </Section>

      <Section width="readable">
        <Card>
          <FormField label={t("settings.personal.locale")}>
            <Select
              value={prefs.locale}
              onChange={(e) => void save({ ...prefs, locale: e.target.value as "it" | "en" })}
            >
              <option value="it">{t("settings.personal.locale.it")}</option>
              <option value="en">{t("settings.personal.locale.en")}</option>
            </Select>
          </FormField>
        </Card>
      </Section>

      <NotificationsSettings />

      <Section width="readable">
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-fg-default">{t("settings.personal.mfa")}</div>
              <div className="text-caption text-fg-muted">{t("settings.personal.mfa.soon")}</div>
            </div>
            <Badge tone={prefs.mfaEnabled ? "success" : "neutral"} variant="soft" size="xs">
              {prefs.mfaEnabled
                ? t("settings.personal.mfa.enabled")
                : t("settings.personal.mfa.disabled")}
            </Badge>
          </div>
        </Card>
      </Section>
    </div>
  );
}
